// src/pages/tenant/TenantDashboard.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

export default function TenantDashboard() {
  const navigate = useNavigate();
  const auth = getAuth();

  // keep track of the logged-in user
  const [currentUser, setCurrentUser] = useState(null);

  // tenant profile + balance
  const [userData, setUserData] = useState(null);
  const [balance, setBalance] = useState(0);

  // work order requests
  const [requests, setRequests] = useState([]);

  // 1) AUTH LISTENER
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        // clear everything on sign-out
        setUserData(null);
        setBalance(0);
        setRequests([]);
      }
    });
    return () => unsubscribeAuth();
  }, [auth]);

  // 2) FETCH PROFILE & BALANCE
  useEffect(() => {
    if (!currentUser) return;

    const fetchProfile = async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);
          setBalance(data.balance ?? 0);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    fetchProfile();
  }, [currentUser]);

  // 3) SUBSCRIBE TO OPEN WORK ORDERS
  useEffect(() => {
    if (!currentUser) return;

    // composite index required on (tenantUid, status, createdAt)
    const ordersQuery = query(
      collection(db, 'workOrders'),
      where('tenantUid', '==', currentUser.uid),
      where('status', '==', 'New'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeOrders = onSnapshot(
      ordersQuery,
      (snap) => {
        console.log(`🔔 ${snap.docs.length} open workOrders fetched`);
        setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('workOrders listener error:', err);
        // if you see an index-error here, click the console link to create it
      }
    );

    return () => unsubscribeOrders();
  }, [currentUser]);

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>
        Hello, Ashley!
        {/* Hello, {userData?.name || currentUser?.displayName || 'Tenant'}! */}
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h6">Your current balance is</Typography>
        <Typography variant="h4" fontWeight="bold">
          ${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Typography>
        <Box mt={2} display="flex" gap={2}>
          <Button
            variant="contained"
            color="success"
            onClick={() => navigate('/tenant/makePayment')}
          >
            Make payment
          </Button>
          <Button variant="outlined">Set up autopay</Button>
           <Button
   component="a"
   href="https://www.paynearme.com/~A29WRirfv2Kw34237Y5Qog=="
   target="_blank"
   rel="noopener"
   variant="outlined"
 >
   Get Paycode
 </Button>
        </Box>
        <Box
          mt={3}
          p={2}
          borderRadius={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ background: '#f5f5f5' }}
        >
          <Typography fontSize={14}>
            Build better credit by having monthly rent payments automatically reported to the credit bureaus.
          </Typography>
          <Button variant="contained" color="success" size="small">
            Get started
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Open Requests */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: 3,
              minHeight: '400px',
              maxHeight: '500px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Open requests
            </Typography>
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {requests.map(req => (
                <Card
                  key={req.id}
                  onClick={() => navigate(`/tenant/requests/${req.id}`)}
                  sx={{
                    my: 1,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    boxShadow: 1,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography fontWeight="bold">{req.subject}</Typography>
                        <Typography fontSize={13} color="text.secondary" mt={0.5}>
                          {req.description}
                        </Typography>
                      </Box>
                      {req.imageUrl && (
                        <img
                          src={req.imageUrl}
                          alt="attachment"
                          style={{
                            height: 60,
                            width: 60,
                            borderRadius: 4,
                            objectFit: 'cover'
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => navigate('/tenant/createRequest')}
            >
              Create request
            </Button>
          </Paper>
        </Grid>

        {/* New Announcements */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: 3,
              minHeight: '400px',
              maxHeight: '500px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6">New announcements</Typography>
            <Box sx={{ overflowY: 'auto', mt: 2 }}>
              <Typography>
                Office Closure Notice – Independence Day <strong style={{ color: 'green' }}>NEW</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Upcoming Events */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: 3,
              minHeight: '400px',
              maxHeight: '500px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6">Upcoming events</Typography>
            <Box sx={{ overflowY: 'auto', mt: 2 }}>
              <Typography color="text.secondary">No upcoming events</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Lease Info */}
        <Grid item xs={12} md={6}>
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: 3,
          minHeight: 240,  // bumped to fit the extra row
        }}
      >
        <Typography variant="h6">Lease information</Typography>
        <Box mt={2}>
          <Typography fontSize={14}>Account number</Typography>
          <Typography fontWeight="bold">00519854</Typography>

          <Typography mt={1} fontSize={14}>Address</Typography>
          <Typography>
            750 Hilton Street<br />
            Memphis, TN 38114<br />
            United States
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <Typography fontSize={14}>Start date</Typography>
              <Typography>12/7/22</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={14}>End date</Typography>
              <Typography>11/30/25</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <Typography fontSize={14}>Rent</Typography>
              <Typography>$1,850.00</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={14}>Prepayments</Typography>
              <Typography>$0.00</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <Typography fontSize={14}>Deposits</Typography>
              <Typography>$1,550.00</Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Grid>
      </Grid>
    </Box>
  );
}